import styles from './NewAgentTaskForm.module.css'
import {NewTaskChatInput} from '@github-ui/agent-sessions/components/NewTaskChatInput'
import type {DropdownRepository} from '@github-ui/agent-sessions/hooks/useCopilotRepoFetching'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {RelayEnvironmentProvider} from 'react-relay'
import {SkeletonBox} from '@primer/react'
import {useCurrentRepository, type Repository} from '@github-ui/current-repository'
import {uuid} from '@github-ui/agent-sessions/utils/uuid'
import {useCopilotChatPayload} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayload'
import type {CreateTaskParams} from '@github-ui/agent-sessions/types/task'

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

export type NewAgentTaskFormProps = {
  createTask: (task: CreateTaskParams) => void
  currentRepo?: Repository
  filePath?: string
  onCreate?: () => void
  showHeader?: boolean
  hideRepoSelector?: boolean
  eventType: string
}

export function NewAgentTaskForm({
  createTask,
  currentRepo,
  filePath,
  onCreate,
  showHeader = true,
  hideRepoSelector,
  eventType,
}: NewAgentTaskFormProps) {
  const copilotChatPayload = useCopilotChatPayload()
  const currentRepoFromContext = useCurrentRepository()

  const initialRepo = currentRepo || currentRepoFromContext

  const handleUserSubmit = async (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedSubAgent: string,
  ) => {
    const trimmedContent = content.trim()
    if (trimmedContent === '') return
    if (!selectedRepo) return
    const newTask = {
      id: uuid(),
      nwo: selectedRepo.nameWithOwner,
      problemStatement: trimmedContent,
      baseRef: selectedBranch || selectedRepo.defaultBranch,
      filepath: filePath || undefined,
      eventType,
      subagent: selectedSubAgent,
    }
    createTask(newTask)

    onCreate?.()
  }

  return (
    <>
      {showHeader && (
        <div className={styles.header}>
          <h2 className="f5">New agent task</h2>
        </div>
      )}
      <div className={`${styles.content} ${showHeader ? '' : 'pt-3'}`}>
        {copilotChatPayload ? (
          <RelayEnvironmentProvider environment={environment}>
            <CopilotChatProvider
              workerPath={copilotChatPayload.findFileWorkerPath}
              threadId={null}
              refs={copilotChatPayload.refs || []}
              mode="immersive"
              ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
              copilotUpsellBannerDismissed
              copilotChatPayload={copilotChatPayload}
            >
              <NewTaskChatInput
                handleUserSubmit={handleUserSubmit}
                initialRepo={initialRepo}
                filePath={filePath}
                hideRepoSelector={hideRepoSelector}
              />
            </CopilotChatProvider>
          </RelayEnvironmentProvider>
        ) : (
          <SkeletonBox className={styles.skeleton} />
        )}
      </div>
    </>
  )
}

try{ NewAgentTaskForm.displayName ||= 'NewAgentTaskForm' } catch {}